import React, { useEffect, useMemo, useState } from "react";
import {
    IAttribute,
    IBinding,
    IPrintedText,
    isMetaNestedValue,
    isTextCustomization,
} from "constants/product-customizations/customization";

import {
    CustomizationSelectionType,
    TPrintedTextCustomizationSelection,
    TPrintedTextFontSelection,
    TPrintedTextFontSizeSelection,
    TPrintedTextColorPickerSelection,
    TPrintedTextColorSwatchSelection,
    TPrintedTextOptionCustomizationSelection,
    PrintedTextOptionValueKey,
    TMetaCustomizationSelection,
    TPrintedTextChildAttributeSelection,
} from "constants/product-customizations/customization-selection.types";

import TextField from "@material-ui/core/TextField";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { formatMessage } from "lib/globalize-util";
import { SelectableAttribute } from "./selectable-attribute";
import { ColorAttribute } from "./color-attribute";
import theme from "styles/theme";
import { PrintedAttributePrintTypes } from "./print-types";
import { usePrintType } from "./printed-attribute";
import { assertExhaustiveSwitch } from "components/utils/validations";
import { StringLengthCounter } from "./string-length-counter";
import { ChildAttributes } from "./child-attributes";
import { getMetaNestedValues } from "./helpers";

export interface IPrintedTextAttributeProps {
    selected: TPrintedTextCustomizationSelection;
    types: string[];
    text: IPrintedText;
    attributeId: string;
    label: string;
    isRequired?: boolean;
    target?: IBinding;
    readonly?: boolean;
    onChange?: (value: TPrintedTextCustomizationSelection) => void;
    onValidationError?: (attributeId: string, error: string | null) => void;
    childAttributes?: IAttribute[];
}

const INPUT_WIDTH = "100%";
const INPUT_HEIGHT = "40px";

export const PrintedTextAttribute: React.FC<IPrintedTextAttributeProps> = ({
    selected,
    types,
    text,
    attributeId,
    label,
    isRequired,
    target,
    readonly,
    onChange,
    onValidationError,
    childAttributes,
}) => {
    const classes = useStyles();
    const [currentText, setCurrentText] = useState("");
    const [selectedFont, setSelectedFont] = useState<TPrintedTextFontSelection | null>(null);
    const [selectedFontSize, setSelectedFontSize] = useState<TPrintedTextFontSizeSelection | null>(null);
    const [selectedColorPicker, setSelectedColorPicker] = useState<TPrintedTextColorPickerSelection | null>(null);
    const [selectedColorSwatch, setSelectedColorSwatch] = useState<TPrintedTextColorSwatchSelection | null>(null);
    const [textValidationError, setTextValidationError] = useState<string | null>(null);
    const [selectedPrintType, setSelectedPrintType] = usePrintType(selected);

    const metaNestedValues: TMetaCustomizationSelection[] = useMemo(() => {
        const selectedNestedValues = selected?.nestedValues?.filter(isMetaNestedValue) ?? [];
        return getMetaNestedValues([], selectedNestedValues, readonly, childAttributes);
    }, [childAttributes, selected, readonly]);

    useEffect(() => {
        const value = selected.value;
        setCurrentText(value);

        const { min: minLimit, max: maxLimit } = text.characterLimit;
        const isLessThanMinLimit = value.length < minLimit;
        const isMinLimitViolated = isRequired ? isLessThanMinLimit : value.length > 0 && isLessThanMinLimit;

        const isInvalid = isMinLimitViolated || value.length > maxLimit;
        setTextValidationError(!isInvalid ? null : formatMessage("Invalid input length."));

        const textNestedValues = selected.nestedValues.filter(isTextCustomization);

        textNestedValues.forEach((v) => {
            switch (v.valueKey) {
                case PrintedTextOptionValueKey.FONT:
                    setSelectedFont(v);
                    break;

                case PrintedTextOptionValueKey.FONT_SIZE:
                    setSelectedFontSize(v);
                    break;

                case PrintedTextOptionValueKey.COLOR_PICKER:
                    setSelectedColorPicker(v);
                    setSelectedColorSwatch(null);
                    break;

                case PrintedTextOptionValueKey.COLOR_SWATCH:
                    setSelectedColorPicker(null);
                    setSelectedColorSwatch(v);
                    break;

                default:
                    assertExhaustiveSwitch(v);
            }
        });
    }, [selected, text, attributeId, label, isRequired, types]);

    const { font, fontSize } = text ?? {};
    const { picker: colorPicker, swatch: colorSwatch } = text?.color ?? {};

    const handleChange = (value: string, printType: string, nestedValues: TPrintedTextChildAttributeSelection[] = []) =>
        onChange?.({
            type: CustomizationSelectionType.PRINTED_TEXT,
            printType,
            attributeId,
            externalId: selected.externalId,
            label,
            value,
            displayValue: value,
            target,
            nestedValues,
        });

    const nestedValues: (TPrintedTextOptionCustomizationSelection | null)[] = [
        selectedFont,
        selectedFontSize,
        colorPicker ? selectedColorPicker : null,
        colorSwatch ? selectedColorSwatch : null,
    ];

    const filteredNestedValues = nestedValues.filter(Boolean);

    const handleNestedAttributeChange = <T extends TPrintedTextOptionCustomizationSelection>(value: T | undefined) => {
        if (!value) {
            return;
        }

        let [selectedFont, selectedFontSize, selectedColorPicker, selectedColorSwatch] = nestedValues;
        switch (value.valueKey) {
            case PrintedTextOptionValueKey.FONT:
                selectedFont = value;
                setSelectedFont(selectedFont);
                break;

            case PrintedTextOptionValueKey.FONT_SIZE:
                selectedFontSize = value;
                setSelectedFontSize(selectedFontSize);
                break;

            case PrintedTextOptionValueKey.COLOR_PICKER:
                selectedColorPicker = value;
                setSelectedColorPicker(selectedColorPicker);
                break;

            case PrintedTextOptionValueKey.COLOR_SWATCH:
                selectedColorSwatch = value;
                setSelectedColorSwatch(selectedColorSwatch);
                break;

            default:
                assertExhaustiveSwitch(value);
        }

        const values = [
            selectedFont,
            selectedFontSize,
            colorPicker ? selectedColorPicker : null,
            colorSwatch ? selectedColorSwatch : null,
        ];

        handleChange(currentText, selectedPrintType, [...values.filter(Boolean), ...metaNestedValues]);
    };

    const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        handleChange(event.target.value, selectedPrintType, [...filteredNestedValues, ...metaNestedValues]);

    const handleTypeChange = (selectedType: string) => {
        setSelectedPrintType(selectedType);
        handleChange(currentText, selectedType, [...filteredNestedValues, ...metaNestedValues]);
    };

    const onChildValuesChanged = (metaValues: TMetaCustomizationSelection[]) => {
        handleChange(currentText, selectedPrintType, [...filteredNestedValues, ...metaValues]);
    };

    useEffect(() => {
        onValidationError?.(attributeId, textValidationError ?? null);
    }, [textValidationError, onValidationError, attributeId]);

    return (
        <div>
            <PrintedAttributePrintTypes
                types={types}
                printType={selectedPrintType}
                readonly={readonly}
                onChange={handleTypeChange}
            />
            <div className={classes.container}>
                <div className={classes.item}>
                    <div className={classes.label}>{formatMessage("Text")}:</div>
                    <TextField
                        name="Text"
                        id="printedText"
                        className={classes.text}
                        value={currentText}
                        onChange={handleTextChange}
                        margin="normal"
                        error={!!textValidationError}
                        helperText={
                            <StringLengthCounter
                                current={currentText.length}
                                limit={text?.characterLimit?.max}
                                error={textValidationError}
                            />
                        }
                        disabled={readonly}
                        style={muiStyles.textInput}
                        InputProps={{
                            style: {
                                height: INPUT_HEIGHT,
                                borderRadius: "5px",
                            },
                            classes: {
                                input: classes.input,
                            },
                            disableUnderline: true,
                        }}
                        FormHelperTextProps={{
                            classes: {
                                error: classes.errorMessage,
                            },
                        }}
                    />
                </div>
                {fontSize && selectedFontSize && (
                    <div className={classes.item}>
                        <div className={classes.label}>{formatMessage("Size")}:</div>
                        <SelectableAttribute
                            isRequired={true}
                            selected={selectedFontSize}
                            attributeValue={fontSize}
                            attributeId={attributeId}
                            label={formatMessage("Font Size")}
                            readonly={readonly}
                            onChange={handleNestedAttributeChange<typeof selectedFontSize>}
                            target={selectedFontSize?.target}
                            valueKey={PrintedTextOptionValueKey.FONT_SIZE}
                            width={INPUT_WIDTH}
                            inputStyle={muiStyles.dropdownInput}
                            containerStyle={muiStyles.dropdownContainer}
                        />
                    </div>
                )}
                {font && selectedFont && (
                    <div className={classes.item}>
                        <div className={classes.label}>{formatMessage("Font")}:</div>

                        <SelectableAttribute
                            attributeId={attributeId}
                            attributeValue={font}
                            isRequired={true}
                            label={formatMessage("Font")}
                            onChange={handleNestedAttributeChange<typeof selectedFont>}
                            readonly={readonly}
                            selected={selectedFont}
                            target={selectedFont.target}
                            valueKey={PrintedTextOptionValueKey.FONT}
                            width={INPUT_WIDTH}
                            inputStyle={muiStyles.dropdownInput}
                            containerStyle={muiStyles.dropdownContainer}
                        />
                    </div>
                )}
                {colorPicker && selectedColorPicker && (
                    <div className={classes.item}>
                        <div className={classes.label}>{formatMessage("Color")}:</div>

                        <ColorAttribute
                            isRequired={true}
                            selected={selectedColorPicker}
                            picker={colorPicker}
                            attributeId={attributeId}
                            label={formatMessage("Color")}
                            readonly={readonly}
                            onChange={handleNestedAttributeChange<typeof selectedColorPicker>}
                            target={selectedColorPicker?.target}
                            isNested={true}
                            valueKey={PrintedTextOptionValueKey.COLOR_PICKER}
                        />
                    </div>
                )}
                {colorSwatch && selectedColorSwatch && (
                    <div className={classes.item}>
                        <div className={classes.label}>{formatMessage("Color")}:</div>

                        <ColorAttribute
                            isRequired={true}
                            selected={selectedColorSwatch}
                            swatch={colorSwatch}
                            attributeId={attributeId}
                            label={formatMessage("Color")}
                            readonly={readonly}
                            onChange={handleNestedAttributeChange<typeof selectedColorSwatch>}
                            target={selectedColorSwatch?.target}
                            isNested={true}
                            valueKey={PrintedTextOptionValueKey.COLOR_SWATCH}
                        />
                    </div>
                )}
                {childAttributes && childAttributes.length > 0 && (
                    <>
                        <div className={classes.divider} />
                        <ChildAttributes
                            childAttributes={childAttributes}
                            nestedValues={metaNestedValues}
                            selected={selected}
                            attributeId={attributeId}
                            readonly={readonly ?? false}
                            onChange={onChildValuesChanged}
                            onValidationError={onValidationError}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const useStyles = makeStyles({
    item: {
        margin: "0 30px 15px 0",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        width: "45%",
    },
    container: {
        display: "flex",
        flexWrap: "wrap",
        width: INPUT_WIDTH,
        marginTop: "10px",
    },
    label: {
        color: theme.palette.textColor,
        fontSize: 12,
        fontWeight: 700,
        marginBlockEnd: 10,
        margin: 0,
    },
    input: {
        fontSize: "16px",
        padding: "8px 5px 8px 15px !important",
        borderRadius: "5px !important",
        "&.Mui-disabled": {
            backgroundColor: `${theme.palette.lighterGrey} !important`,
            color: theme.palette.darkGrey,
        },
        width: INPUT_WIDTH,
        height: "40px !important",
        boxSizing: "border-box",
    },
    errorMessage: {
        fontSize: "14px",
    },
    text: {
        width: INPUT_WIDTH,
        marginBlockStart: 0,
        fontSize: 14,
    },
    selectWrapper: {
        marginTop: "20px",
    },
    selectTitle: {
        fontSize: "16px",
    },
    divider: {
        height: 1,
        width: "100%",
        marginBlock: 16,
        backgroundColor: theme.palette.borderColor,
    },
});

const muiStyles = {
    dropdownInput: { verticalAlign: "middle" },
    dropdownContainer: { marginBottom: "8px" },
    textInput: { height: INPUT_HEIGHT, borderRadius: "4px" },
};
